/* global Sys*/
/**
 * An implementation of JavaScript Deferred/Promise design pattern.
 *
 * Allows for an easier way to chain asynchronous callbacks. You can attach multiple handlers for when a Deferred
 * is resolved or rejected. After creating a Deferred object you can set up which object that you want to wait for
 * and attach handlers for what happens when the object(s) that you listen to resolve or reject.
 *
 * @example
 *     // Example
 *     //
 *     // A simple example where we wait for a JavaScript file to be loaded
 *     //
 *     var deferred = new Sys.Deferred();
 *     var jsLoad = Sys.utils.loadJs({
 *          url : "src/javascript/file.js"
 *     });
 *     deferred.when(jsLoad).then(function() {
 *          initJsFile();
 *     });
 *
 *     // Example
 *     //
 *     // Then method can also be chained and several then methods can be added.
 *     //
 *     var deferred = new Sys.Deferred();
 *     deferred.when(Sys.utils.loadJs({
 *          url : "src/javascript/file.js"
 *     })).then(function() {
 *          initJsFile();
 *     }).then(function() {
 *         doSomethingElse();
 *     });
 *
 *     // Example
 *     //
 *     // There is also the possibility to add error handling to the function handlers.
 *     // We assume that searchForNode has implemented the Deferred interface
 *     //
 *     var deferred = new Sys.Deferred();
 *     deferred.when(searchForNode()).then(function() {
 *          // only called when load is successful
 *          modifyNode();
 *     }).fail(function() {
 *          // only called when load is unsuccessful
 *         handleError();
 *     }).always(function() {
 *          // always executed
 *         proceed();
 *     });
 *
 * @class Sys.Deferred
 */
Sys.Deferred = function() {
    this.whenList = [];
    this.thenList = [];
    this.failList = [];
    this.alwaysList = [];
    this.states = {
        pending: 0,
        resolved: 1,
        rejected: 2
    };
    this.resolved = 0;
    this.rejected = 0;
    this.args = [];
    this.state = this.states.pending;
};

Sys.Deferred.prototype = {

    /**
     * Takes in a single deferred object and registers.
     *
     * @param {...Sys.Deferred|Sys.Deferred[]} deferred Either several Deferred objects as comma-separated parameters or Deferred objects in an array.
     * @returns {Sys.Deferred} This Deferred.
     */
    when: function(deferred) {
        // start by determining if (a) that it is a comma-separated list of Deferreds or (b) the input argument is an array of Deferreds
        // case a:
        // new Sys.Deferred().when(ajaxCall1, ajaxCall2, ajaxCall3);
        // case b:
        // new Sys.Deferred().when([ajaxCall1, ajaxCall2, ajaxCall3]);
        var deferreds;

        if (Sys.isArray(deferred)) {
            deferreds = deferred;
        }
        else {
            deferreds = Array.prototype.slice.call(arguments, 0);
        }

        // for each deferred, register a handler to whenever an update is made to the state of it
        // we handle if the deferred has been rejected or resolved in onDeferredUpdated
        Sys.each(deferreds, function(d) {
            this.whenList.push(d);

            d.always(function() {
                this.onDeferredUpdated();
            }, this);
        }, this);

        // update status when all deferreds have been added. When doing this we make sure if for some reason
        // all deferreds that have been added are resolved or rejected we can set the correct state on this Deferred
        // immediately since d.always above will not ever be called on.
        this.onDeferredUpdated();

        return this;
    },

    /**
     * Add a function that will execute when the method returns as finished successfully.
     *
     * @param {Function} f The function to execute.
     * @param {Object} [scope=window] The scope to execute the function in.
     * @returns {Sys.Deferred} This Deferred.
     */
    then: function(f, scope) {
        scope = scope || window;
        if (this.isWaiting()) {
            this.thenList.push({
                fn: f,
                scope: scope
            });
        }
        else if (this.isResolved()) {
            f.call(scope);
        }
        return this;
    },

    /**
     * Add a function that will execute when the method returns as failed.
     *
     * @param {Function} f The function to execute.
     * @param {Object} [scope=window] The scope to execute the function in.
     * @returns {Sys.Deferred} This Deferred.
     */
    fail: function(f, scope) {
        scope = scope || window;
        if (this.isWaiting()) {
            this.failList.push({
                fn: f,
                scope: scope
            });
        }
        else if (this.isRejected()) {
            f.call(scope);
        }
        return this;
    },

    /**
     * Add a function that will execute no matter if method fails or succeeds.
     *
     * @param {Function} f The function to execute.
     * @param {Object} [scope=window] The scope to execute the function in.
     * @returns {Sys.Deferred} This Deferred.
     */
    always: function(f, scope) {
        scope = scope || window;
        if (this.isWaiting()) {
            this.alwaysList.push({
                fn: f,
                scope: scope
            });
        }
        else {
            f.call(scope);
        }
        return this;
    },

    /**
     * Resolve the deferred, setting state resolved on it.
     *
     * @returns {void}
     */
    resolve: function() {
        this.state = this.states.resolved;
        this.onStateUpdated();
    },

    /**
     * Resolves the deferred and passes along a parameter that will be forwarded to any "then" and "always" handelers.
     *
     * @param {Array} args Arguments as an array.
     * @returns {void}
     */
    resolveWith: function(args) {
        this.args = args;
        this.resolve();
    },

    /**
     * Reject the deferred, setting state rejected on it.
     *
     * @returns {void}
     */
    reject: function() {
        if (typeof this.fallbackFilter === "function") {
            this.onFallback();
        }
        else {
            this.state = this.states.rejected;
            this.onStateUpdated();
        }
    },

    /**
     * Rejects the deferred and passes along a parameter that will be forwarded to any "fail" and "always" handelers.
     *
     * @param {Array} args Arguments as an array.
     * @returns {void}
     */
    rejectWith: function(args) {
        this.args = args;
        this.reject();
    },

    /**
     * Answers if Deferred is rejected or not.
     *
     * @returns {boolean} Returns true if Deferred is rejected, false otherwise.
     */
    isRejected: function() {
        return (this.state === this.states.rejected);
    },

    /**
     * Answers if Deferred is resolved or not.
     *
     * @returns {boolean} Returns true if Deferred is resolved, false otherwise.
     */
    isResolved: function() {
        return (this.state === this.states.resolved);
    },

    /**
     * Setup a fallback option (a new deferred) if the deferred is about to be rejected. The status of the
     * fallback determines the status of the original deferred.
     *
     * @example
     *     var A = new Sys.Deferred();
     *
     *     A.fallback(function() {
     *       return new Sys.Deferred();
     *     });
     *
     *     A.reject();
     *     console.log(A.isRejected());
     *     > false
     *
     *     B.resolve();
     *     console.log(A.isRejected());
     *     > true
     *     console.log(B.isRejected());
     *     > true
     *
     * @param {Function} fallback The function to run when the deferred is rejected.
     * @returns {Sys.Deferred} This Deferred.
     */
    fallback: function(fallback) {
        this.fallbackFilter = fallback;

        return this;
    },

    /**
     * Determines the status of the original deferred based on the status of the new one.
     *
     * @private
     * @returns {void}
     */
    onFallback: function() {
        var me = this,
            deferred = me.fallbackFilter.call(this, this.args);

        if (Sys.isObj(deferred)) {
            deferred
                .done(function() {
                    me.resolveWith(deferred.args);
                })
                .fail(function() {
                    me.rejectWith(deferred.args);
                });
        }
    },

    /**
     * Called when a Deferred registered in when() is either resolved or rejected. If all Deferreds have been resolved or rejected this method will reject or resolve this Deferred.
     *
     * @private
     * @returns {void}
     */
    onDeferredUpdated: function() {
        var rejected = 0,
            resolved = 0,
            args;

        Sys.each(this.whenList, function(deferred) {
            if (deferred.isRejected()) {
                rejected += 1;
            }
            else if (deferred.isResolved()) {
                resolved += 1;
            }
        }, this);

        this.resolved = resolved;
        this.rejected = rejected;

        if (!this.isWaiting()) {
            args = [];
            Sys.each(this.whenList, function(deferred) {
                args = args.concat(deferred.args);
            });

            if (rejected > 0) {
                this.rejectWith(args);
            }
            else if (resolved > 0) {
                this.resolveWith(args);
            }
        }
    },

    /**
     * Answers the question if we are still waiting for deferreds to be resolved or not.
     *
     * @private
     * @returns {boolean} True if there are still unresolved deferreds we are waiting for, false if all are either resolved or rejected.
     */
    isWaiting: function() {
        // true if we do not have any whens
        // true if there are less (resolved and rejected) than whens we are waiting for
        return !this.whenList.length || ((this.resolved + this.rejected) < this.whenList.length);
    },

    /**
     * Executed when a state has been updated internally. Runs either the "fail" or "then" methods, plus the "always" methods.
     *
     * @private
     * @returns {void}
     */
    onStateUpdated: function() {
        var list = [],
            def,
            i,
            numItems;

        if (this.state === this.states.resolved) {
            list = this.thenList.concat(this.alwaysList);
        }
        else if (this.state === this.states.rejected) {
            list = this.failList.concat(this.alwaysList);
        }

        numItems = list.length;

        for (i = -1; ++i < numItems;) {
            def = list[i].fn.apply(list[i].scope, this.args);

            if (Sys.isObj(def) && (i + 1 < numItems)) {
                def.thenList.push.apply(def.thenList, list.slice(i + 1));
                break;
            }
        }

        // clear all lists
        this.thenList = [];
        this.alwaysList = [];
        this.failList = [];
    }
};

Sys.Deferred.prototype.done = Sys.Deferred.prototype.then;
